import { allTimezones, useTimezoneSelect } from 'react-timezone-select';
import FormItem from './FormItem';
import { Switcher } from '@silverfort/sf-components';
import Collapse from '@material-ui/core/Collapse';
import { FREQUENCY_OPTIONS, DAYS_OPTIONS, PERIOD_OPTIONS } from '@silverfort-nodelibs/common/utils/consts';
import SelectSettingsComponent from './SelectSettingsComponent';
import React, { FC, SetStateAction } from 'react';

export interface SettingsFormProps {
    frequency: number;
    setFrequency: React.Dispatch<SetStateAction<number>>;
    setDayInWeek: React.Dispatch<SetStateAction<string | undefined>>;
    setDayInMonth: React.Dispatch<SetStateAction<string | undefined>>;
    setAssessmentPeriod: React.Dispatch<SetStateAction<string | undefined>>;
    setTimezone: React.Dispatch<SetStateAction<string | undefined>>;
    setHour: React.Dispatch<SetStateAction<string | undefined>>;
    setRecipients: React.Dispatch<SetStateAction<string[]>>;
    dayInMonth?: string;
    assessmentPeriod?: string;
    timezone?: string;
    hour?: string;
    dayInWeek?: string;
    recipients?: string[];
}

const RiskReportSettingsForm: FC<SettingsFormProps> = ({
    frequency,
    setFrequency,
    dayInWeek,
    setDayInWeek,
    dayInMonth,
    setDayInMonth,
    assessmentPeriod,
    setAssessmentPeriod,
    timezone,
    setTimezone,
    hour,
    setHour,
    recipients,
    setRecipients
}) => {
    const { options: timezoneOptions, parseTimezone } = useTimezoneSelect({
        labelStyle: 'original',
        timezones: allTimezones
    });
    return (
        <div className="w-full h-fit p-[16px] border rounded-lg flex flex-col gap-[16px]">
            <FormItem label="Frequency">
                <div className="w-[236px] flex flex-1 bg-sf-light-100 rounded-lg">
                    {Object.values(FREQUENCY_OPTIONS).map((frequencyOption) => (
                        <Switcher
                            key={frequencyOption.value}
                            checked={frequency === frequencyOption.value}
                            text={frequencyOption.label}
                            onClick={() => setFrequency(frequencyOption.value)}
                            className="p-[2px]"
                        />
                    ))}
                </div>
            </FormItem>
            <div>
                <Collapse in={frequency === FREQUENCY_OPTIONS.WEEKLY.value}>
                    <FormItem label="Day Of Week">
                        <SelectSettingsComponent
                            selectedSettings={dayInWeek}
                            setSelectedSettings={
                                ((day) => {
                                    setDayInWeek(day as string);
                                }) as (value?: string | string[]) => void
                            }
                            options={Object.values(DAYS_OPTIONS).map((dayOption) => ({
                                value: `${dayOption.value}`,
                                title: dayOption.label
                            }))}
                        />
                    </FormItem>
                </Collapse>
                <Collapse in={frequency === FREQUENCY_OPTIONS.MONTHLY.value}>
                    <FormItem label="Day Of Month">
                        <SelectSettingsComponent
                            selectedSettings={dayInMonth}
                            setSelectedSettings={setDayInMonth as (value?: string | string[]) => void}
                            options={Array.from({ length: 31 }, (_, index) => ({
                                value: `${index + 1}`,
                                title: `${index + 1}`
                            }))}
                        />
                    </FormItem>
                </Collapse>
            </div>
            <FormItem label="Time">
                <div className="flex justify-between flex-1 gap-[8px]">
                    <SelectSettingsComponent
                        selectedSettings={hour}
                        setSelectedSettings={setHour as (value?: string | string[]) => void}
                        options={Array.from({ length: 24 }, (_, index) => ({
                            value: `${index}`,
                            title: `${index}:00`
                        }))}
                    />
                    <SelectSettingsComponent
                        options={timezoneOptions.map((timezoneOption) => ({
                            value: timezoneOption.value,
                            title: timezoneOption.label
                        }))}
                        selectedSettings={timezone}
                        setSelectedSettings={
                            ((selectedTimezone: string) => setTimezone(parseTimezone(selectedTimezone).value)) as (
                                value?: string | string[]
                            ) => void
                        }
                    />
                </div>
            </FormItem>
            <FormItem label="Assessment period">
                <SelectSettingsComponent
                    options={Object.values(PERIOD_OPTIONS).map((periodOption) => ({
                        value: `${periodOption.value}`,
                        title: periodOption.label
                    }))}
                    selectedSettings={assessmentPeriod}
                    setSelectedSettings={setAssessmentPeriod as (value?: string | string[]) => void}
                />
            </FormItem>
            <FormItem label="Recipients">
                <SelectSettingsComponent
                    options={
                        recipients?.map((recipient) => ({
                            value: recipient,
                            title: recipient
                        })) ?? []
                    }
                    selectedSettings={recipients}
                    setSelectedSettings={setRecipients as (value?: string | string[]) => void}
                    addNewItemHandler={(value) => {
                        if (value) {
                            setRecipients((prevState) => [...prevState, value]);
                        }
                    }}
                    multiple
                />
            </FormItem>
        </div>
    );
};

export default RiskReportSettingsForm;
